import { defineMessage, type IntlShape, type MessageDescriptor } from "react-intl";
import type { Facet, Skill } from "#graphql/client";
import * as M from "../materials";
import type { FigureControlItem } from "./figure-state";

// -----------------------------------------------------------------------------
// Goal Annotation

export const goalRule = (intl: IntlShape, facet: Facet, skill: Skill, allGrades = false) => ({
  start: {
    datum: {
      x: facet === "GRADE" ? 3 : 9,
      y: 1,
    },
    symbol: true,
    annotation: {
      text: intl.formatMessage(goalAnnotation[skill][facet]),
      isAlwaysVisible: true,
    },
  },
  end: {
    datum: {
      x: facet === "GRADE" ? (allGrades ? 10 : 6) : 14,
      y: 1,
    },
    symbol: false,
  },
  style: {
    stroke: M.q06Palette[6],
    strokeDasharray: "6 8",
    strokeWidth: 3,
  },
});

// -----------------------------------------------------------------------------
// Translations Lookups

export const goalAnnotation: Record<Skill, Record<Facet, MessageDescriptor>> = {
  LIT: {
    AGE: defineMessage({
      id: "fig.learning.trajectories.goalAnnotation.lit.age",
      defaultMessage: "SDG 4.1.1a calls for all students to have basic reading skills by Grade 3 (typically age 9)",
    }),
    GRADE: defineMessage({
      id: "fig.learning.trajectories.goalAnnotation.lit.grade",
      defaultMessage: "SDG 4.1.1a calls for all students to have basic reading skills by Grade 3",
    }),
  },
  NUM: {
    AGE: defineMessage({
      id: "fig.learning.trajectories.goalAnnotation.num.age",
      defaultMessage: "SDG 4.1.1a calls for all students to have basic mathematics skills by Grade 3 (typically age 9)",
    }),
    GRADE: defineMessage({
      id: "fig.learning.trajectories.goalAnnotation.num.grade",
      defaultMessage: "SDG 4.1.1a calls for all students to have basic mathematics skills by Grade 3",
    }),
  },
};

export const yAxisTitleLookup: Record<Skill, MessageDescriptor> = {
  LIT: defineMessage({
    id: "fig.learning.trajectories.yAxisTitle.lit",
    defaultMessage: "reading at typical Grade 2-3 level",
  }),
  NUM: defineMessage({
    id: "fig.learning.trajectories.yAxisTitle.num",
    defaultMessage: "math skills at typical Grade 2-3 level",
  }),
};

export const xAxisTitleLookup: Record<Facet, MessageDescriptor> = {
  AGE: defineMessage({ id: "fig.learning.trajectories.xAxisTitle.age", defaultMessage: "Age" }),
  GRADE: defineMessage({ id: "fig.learning.trajectories.xAxisTitle.grade", defaultMessage: "Grade" }),
};

export const xAxisSubtitleLookup: Record<Facet, null | MessageDescriptor> = {
  AGE: null,
  GRADE: defineMessage({
    id: "fig.learning.trajectories.xAxisSubtitle.grade",
    defaultMessage: "Highest Grade Attained",
  }),
};

// Data Explorer Controls Options

export const verticalAxisControlLabel = defineMessage({
  id: "fig.learning.trajectories.explorer.controls.simulations.verticalAxis.title",
  defaultMessage: "Vertical Axis",
});

export const countryControlTitle = defineMessage({
  id: "fig.learning.trajectories.explorer.controls.country.title",
  defaultMessage: "Country",
});

export const comparisonCountryControlTitle = defineMessage({
  id: "fig.learning.trajectories.explorer.controls.comparisonCountry.title",
  defaultMessage: "Comparison country",
});

export const countryControlPlaceholder = defineMessage({
  id: "fig.learning.trajectories.explorer.controls.country.placeholder",
  defaultMessage: "Select country",
});

export const simulationControlsLabels = (intl: IntlShape) => ({
  label: intl.formatMessage(
    defineMessage({
      id: "fig.learning.trajectories.explorer.controls.simulation.label",
      defaultMessage: "Simulate policies",
    }),
  ),
  title: intl.formatMessage(
    defineMessage({ id: "fig.learning.trajectories.explorer.controls.simulation.title", defaultMessage: "Simulate" }),
  ),
});

export const verticalAxisControlItems = (intl: IntlShape): Array<FigureControlItem> => [
  {
    value: "LIT",
    label: intl.formatMessage(
      defineMessage({ id: "fig.learning.trajectories.explorer.controls.verticalAxis.lit", defaultMessage: "Reading" }),
    ),
  },
  {
    value: "NUM",
    label: intl.formatMessage(
      defineMessage({
        id: "fig.learning.trajectories.explorer.controls.verticalAxis.num",
        defaultMessage: "Mathematics",
      }),
    ),
  },
];

export const horizontalAxisControlItems = (intl: IntlShape): Array<FigureControlItem> => [
  {
    value: "GRADE",
    label: intl.formatMessage(
      defineMessage({
        id: "fig.learning.trajectories.explorer.controls.horizontalAxis.grade",
        defaultMessage: "Grade",
      }),
    ),
  },
  {
    value: "AGE",
    label: intl.formatMessage(
      defineMessage({ id: "fig.learning.trajectories.explorer.controls.horizontalAxis.age", defaultMessage: "Age" }),
    ),
  },
];

export const viewControlItems = (intl: IntlShape): Array<FigureControlItem> => [
  {
    value: "countries",
    label: intl.formatMessage(
      defineMessage({ id: "fig.learning.trajectories.explorer.controls.view.countries", defaultMessage: "Countries" }),
    ),
  },
  {
    value: "disparities",
    label: intl.formatMessage(
      defineMessage({
        id: "fig.learning.trajectories.explorer.controls.view.disparities",
        defaultMessage: "Groups inside country",
      }),
    ),
  },
];

export const countriesSimulationControlItems = (intl: IntlShape): Array<FigureControlItem> => [
  {
    value: "SIMULATION_ACCESS",
    label: intl.formatMessage(
      defineMessage({
        id: "fig.learning.trajectories.explorer.controls.simulation.countries.access",
        defaultMessage: "Access at 100%",
      }),
    ),
  },
  {
    value: "SIMULATION_LEARNING",
    label: intl.formatMessage(
      defineMessage({
        id: "fig.learning.trajectories.explorer.controls.simulation.countries.learning",
        defaultMessage: "Learning of comparison country",
      }),
    ),
  },
];

export const disparitiesSimulationControlItems = (intl: IntlShape): Array<FigureControlItem> => [
  {
    value: "SIMULATION_ACCESS",
    label: intl.formatMessage(
      defineMessage({
        id: "fig.learning.trajectories.explorer.controls.simulation.disparities.access",
        defaultMessage: "Equal access",
      }),
    ),
  },
  {
    value: "SIMULATION_LEARNING",
    label: intl.formatMessage(
      defineMessage({
        id: "fig.learning.trajectories.explorer.controls.simulation.disparities.learning",
        defaultMessage: "Equal learning",
      }),
    ),
  },
];

export const disparityGroupsControlItems = (intl: IntlShape): Array<FigureControlItem> => [
  {
    value: "WEALTH",
    label: intl.formatMessage(
      defineMessage({ id: "fig.learning.trajectories.explorer.controls.groups.wealth", defaultMessage: "Rich / Poor" }),
    ),
  },
  {
    value: "GENDER",
    label: intl.formatMessage(
      defineMessage({
        id: "fig.learning.trajectories.explorer.controls.groups.gender",
        defaultMessage: "Boys / Girls",
      }),
    ),
  },
  {
    value: "GEOGRAPHY",
    label: intl.formatMessage(
      defineMessage({
        id: "fig.learning.trajectories.explorer.controls.groups.geography",
        defaultMessage: "Urban / Rural",
      }),
    ),
  },
];

/*
  FIXME: Once the API is able to support this logic, this should be removed and there should be
  a new query to get list of countries available per simulation.
*/
export const COUNTRIES_EXCLUDED_DISPARITY_SIM = ["KIR", "MKD", "TCA", "TON", "TUV"];
